import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { DEV_URL } from "@/lib/constants";
import { useMemo, useState, useEffect } from "react";
import { NavbarSection } from "@/components/catalyst-ui/navbar";
import { Button } from "@/components/catalyst-ui/button";
import {
  ArrowUpRightIcon,
  UserCircleIcon,
  SparklesIcon,
  QuestionMarkCircleIcon,
  MegaphoneIcon,
  HomeModernIcon,
  ArrowRightStartOnRectangleIcon,
  ChevronDownIcon,
  ArrowLeftIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  FolderIcon,
  XMarkIcon,
  TvIcon,
  ShieldCheckIcon,
  ChatBubbleLeftRightIcon,
  GiftIcon,
} from "@heroicons/react/20/solid";
import { openIntercomChat } from "@/lib/intercom";
import { openWhatsNewModal } from "@/components/WhatsNewModal/events";
import {
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownItem,
} from "@/components/catalyst-ui/dropdown";
import CodeDesignBrandIcon from "@/assets/icons/16/CodeDesign-brand-lcon.svg";
import { CrownIcon } from "@/assets/icons";
import { useActiveProject } from "@/state/selection.store";
import { useOverview } from "@/features/overview/hooks/useOverview";
import { formatPlanDisplayName } from "@/lib/planDisplay";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useUserProfile } from "@/features/profile/hooks/useUserProfile";
import { useProjectSettingsQuery } from "@/features/settings/hooks/useProjectSettingsQuery";
import { usePageBlueprint } from "@/features/site-pages/hooks/usePageSettings";
import { getUniqueUrl } from "@/features/site-pages/api";
import { useProjects } from "@/features/workspace/hooks/useProjects";
import { useSharedProjects } from "@/features/workspace/hooks/useSharedProjects";
import { CreateProjectDialog } from "@/features/workspace/components/CreateProjectDialog";
import { useAiCompanionMode } from "@/features/site-pages/hooks/useAiCompanionMode";
import { PublishDialog } from "@/features/publish/pages/PublishDialog";
import { PermissionGuard } from "@/app/router/guards";
import { PERMISSIONS } from "@/lib/permissions";
import { useWorkspaces } from "@/features/workspace/hooks/useWorkspaces";
import PricingIframeModal from "@/features/pricing/components/PricingIframeModal";
import { useUIStore } from "@/state/ui.store";
import { useAdminMode } from "@/hooks/useAdminMode";
import { useAiAvailability } from "@/features/ai-connections/hooks/useAiConnections";
import { trackEvent } from "@/lib/analytics";
import { safeLocalStorage } from "@/lib/safe-storage";

export function ProjectNavbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { project: currentProject } = useActiveProject();
  const { overview, refetch } = useOverview();
  const { logout } = useAuth();
  const { profile } = useUserProfile();

  const workspaceId = searchParams.get("workspace");
  const currentProjectId = searchParams.get("project");

  const { workspaces } = useWorkspaces();
  const workspace =
    workspaces?.find(ws => (ws._id || ws.id) === workspaceId) || null;

  const uniqueUrl = getUniqueUrl();
  const { data: projectSettings } = useProjectSettingsQuery(uniqueUrl);
  const { isAiCompanionMode } = useAiCompanionMode(uniqueUrl);

  const { projects = [], loading: projectsLoading } = useProjects(workspaceId);
  const { isAdminMode, targetUserId, targetUserLabel } = useAdminMode();

  const { data: blueprintData } = usePageBlueprint(uniqueUrl);

  const sortedProjects = useMemo(() => {
    if (!projects.length) return [];
    const normalized = projects.map(p => ({ ...p, id: p._id || p.id }));
    const current = normalized.find(p => p.id === currentProjectId);
    const others = normalized.filter(p => p.id !== currentProjectId);
    return current ? [current, ...others] : normalized;
  }, [projects, currentProjectId]);

  // Redirect to sites if project doesn't belong to this workspace
  const sharedParamPresent = searchParams.get("shared") === "true";
  const { projects: sharedProjects, loading: sharedProjectsLoading } =
    useSharedProjects({
      page: 1,
      search: "",
    });

  // Verify the project is actually shared with this user (not just a spoofed URL param)
  const isVerifiedSharedProject = useMemo(() => {
    if (!sharedParamPresent || !currentProjectId) return false;
    if (sharedProjectsLoading) return true; // Assume valid while loading to prevent premature redirect
    return sharedProjects.some(p => (p._id || p.id) === currentProjectId);
  }, [
    sharedParamPresent,
    currentProjectId,
    sharedProjects,
    sharedProjectsLoading,
  ]);

  const isProjectInWorkspace = useMemo(() => {
    if (!currentProjectId || projectsLoading || !projects.length) return true;
    return projects.some(p => (p._id || p.id) === currentProjectId);
  }, [projects, currentProjectId, projectsLoading]);

  // Effect to redirect when project is not in workspace and not a verified shared project
  useEffect(() => {
    if (
      !projectsLoading &&
      !sharedProjectsLoading &&
      currentProjectId &&
      workspaceId &&
      !isProjectInWorkspace &&
      !isVerifiedSharedProject
    ) {
      console.warn(
        "[ProjectNavbar] Project not found in workspace or shared projects, redirecting to home"
      );
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("project");
      newParams.delete("shared");
      navigate(`/?${newParams.toString()}`, { replace: true });
    }
  }, [
    isProjectInWorkspace,
    isVerifiedSharedProject,
    projectsLoading,
    sharedProjectsLoading,
    currentProjectId,
    workspaceId,
    searchParams,
    navigate,
  ]);

  const projectFromOverview = overview?.project || {};

  // Publishing is LICENSE-GATED for the AI-companion family of projects
  // (Website Studio / hasSitemap projects included — they are companion
  // projects). Unpaid ones get the pricing popup, never the publish dialog.
  const isCompanionFamilyProject = Boolean(
    projectFromOverview.hasSitemap ||
      projectFromOverview.aiCompanionMode ||
      currentProject?.aiCompanionMode
  );
  const projectPublishLicensed = Boolean(
    projectFromOverview.order?.paid || currentProject?.order?.paid
  );
  const handlePublishClick = () => {
    if (isCompanionFamilyProject && !projectPublishLicensed) {
      setShowPricingModal(true, "STUDIO_PUBLISH");
      return;
    }
    setShowPublishDialog(true);
  };

  const projectName =
    projectFromOverview.name || currentProject?.name || "Project name";

  // Get workspace active plans
  const workspaceActivePlans = useMemo(() => {
    const plans = workspace?.activePlans || workspace?.plans || [];
    return plans.filter(p => p?.isActive);
  }, [workspace]);

  const primaryPlanName = formatPlanDisplayName(
    workspaceActivePlans?.[0]?.planName ||
    workspaceActivePlans?.[0]?.planType ||
    projectFromOverview.plan ||
    currentProject?.plan ||
    "Free"
  );
  const plan = projectFromOverview.plan || currentProject?.plan || "Free plan";

  const rawPublished =
    projectFromOverview.publishedLink || currentProject?.publishedLink || "";
  const hasPublished = !!rawPublished;
  const publishedHref =
    hasPublished && /^https?:\/\//i.test(rawPublished)
      ? rawPublished
      : hasPublished
        ? `https://${rawPublished}`
        : null;

  const faviconUrl =
    projectSettings?.projectMeta?.favIcon ||
    projectSettings?.projectMeta?.faviconUrl ||
    currentProject?.favIcon ||
    projectFromOverview.favIcon ||
    null;

  const renderProjectIcon = (name, size = 24) => {
    const initial =
      String(name || "P")
        .trim()
        .charAt(0)
        .toUpperCase() || "P";
    if (faviconUrl) {
      return (
        <img
          src={faviconUrl}
          alt={name || "Project favicon"}
          className="rounded-full border border-[#89898A] bg-white object-cover"
          style={{ width: size, height: size }}
          onError={e => {
            e.currentTarget.onerror = null;
            e.currentTarget.removeAttribute("src");
          }}
        />
      );
    }
    return (
      <span
        className="flex items-center justify-center rounded-full bg-[#3F3F46] text-[#A1A1AA] border-[1.5px] border-[#09090B] font-medium uppercase"
        style={{
          width: size,
          height: size,
          fontSize: size >= 24 ? "12px" : "10px",
        }}
      >
        {initial}
      </span>
    );
  };

  const daysAgoLabel = useMemo(() => {
    if (!blueprintData) return null;

    const active = blueprintData.active || {};
    const projectSubdomain = blueprintData.subdomain || "";
    const projectDomain = blueprintData.domain || {};
    const wordpressPlugin = blueprintData.integrations?.wordpressPlugin || {};

    let lastPublishedAt = null;

    const isSubdomainActive = projectSubdomain && active.subdomain === "true";
    const isDomainActive = projectDomain.domain && active.domain === "true";
    const isWordpressActive =
      wordpressPlugin.hostname && active.wordpress === "true";

    if (isDomainActive && projectDomain.updatedAt) {
      lastPublishedAt = new Date(projectDomain.updatedAt);
    } else if (isSubdomainActive && blueprintData.subdomainUpdatedAt) {
      lastPublishedAt = new Date(blueprintData.subdomainUpdatedAt);
    } else if (isWordpressActive && wordpressPlugin.lastUpdatedAt) {
      lastPublishedAt = new Date(wordpressPlugin.lastUpdatedAt);
    }

    if (!lastPublishedAt || Number.isNaN(lastPublishedAt.getTime()))
      return null;

    const diffDays = Math.floor(
      (Date.now() - lastPublishedAt.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (diffDays <= 0) return "Published today";
    if (diffDays === 1) return "Published 1 day ago";
    return `Published ${diffDays} days ago`;
  }, [blueprintData]);

  const handleProjectSelect = project => {
    const projectId = project._id || project.id;
    const params = new URLSearchParams(searchParams);
    params.set("workspace", workspaceId);
    params.set("project", projectId);
    // Remove shared project params — uniqueUrl is resolved from the store, not the URL
    params.delete("uniqueUrl");
    params.delete("shared");
    navigate(`/overview?${params.toString()}`);
  };

  const handleGoToWorkspaces = () => {
    const targetWs = workspaceId || "default";
    const dashboardUrl = `/?workspace=${encodeURIComponent(targetWs)}`;
    // Don't navigate if already on the dashboard
    if (location.pathname === "/projects" && !searchParams.get("project")) return;
    navigate(dashboardUrl);
  };

  const handleGoToManageWorkspaces = () => {
    navigate(
      `/settings/workspace-management?workspace=${encodeURIComponent(workspaceId)}`
    );
  };

  const handleGoToProfile = () => {
    navigate(
      workspaceId
        ? `/profile?workspace=${encodeURIComponent(workspaceId)}`
        : "/profile"
    );
  };

  const { enabled: aiConnectionsEnabled } = useAiAvailability();
  const handleGoToAiConnections = () => {
    trackEvent("AiConnections:EntryClicked", { from: "profile_menu" });
    navigate(
      workspaceId
        ? `/ai-connections?workspace=${encodeURIComponent(workspaceId)}`
        : "/ai-connections"
    );
  };

  const handleSignOut = () => {
    logout();
  };

  const handleOpenDesignEditor = () => {
    let userParam = "";
    if (sharedParamPresent) {
      try {
        const uid = JSON.parse(safeLocalStorage.getItem("user_data") || "{}").id;
        if (uid) userParam = `&user=${uid}`;
      } catch {
        // Keep opening the editor even if stored user data is malformed.
      }
    }
    window.open(
      `${DEV_URL}/app/builder?project=${uniqueUrl}${userParam}#home`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleOpenPublished = () => {
    if (!publishedHref) return;
    window.open(publishedHref, "_blank", "noopener,noreferrer");
  };

  const handleOpenHelpSupport = () => {
    window.open("https://docs.codedesign.ai", "_blank", "noopener,noreferrer");
  };

  const handleOpenFeatureRequest = () => {
    window.open(
      "https://codedesign.canny.io/feature-request",
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleOpenWhatsNew = () => {
    openWhatsNewModal({ source: "project_navbar_profile_dropdown" });
  };

  const [projectSearch, setProjectSearch] = useState("");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const showPublishDialog = useUIStore(state => state.showPublishDialog);
  const setShowPublishDialog = useUIStore(state => state.setShowPublishDialog);
  const setShowPricingModal = useUIStore(state => state.setShowPricingModal);

  const filteredProjects = useMemo(() => {
    const query = projectSearch.trim().toLowerCase();
    if (!query) return sortedProjects;
    return sortedProjects.filter(p =>
      (p.name || "Untitled Project").toLowerCase().includes(query)
    );
  }, [projectSearch, sortedProjects]);

  const visibleProjects = filteredProjects.slice(0, 6);

  const handleOpenPricingModal = () => setShowPricingModal(true, "PROJECT_NAVBAR_UPGRADE");

  return (
    <>
      <div className="h-[61px] bg-[#27272A] flex items-center w-full text-white">
        <button
          type="button"
          onClick={handleGoToWorkspaces}
          className="group relative h-full sm:-ml-4 w-14 hidden lg:flex items-center justify-center border-r border-[#3F3F46] data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2"
        >
          <ArrowLeftIcon className="absolute w-8 h-8 opacity-0 group-hover:opacity-100 transition-opacity duration-150 bg-[#18181B] rounded-full p-2" />
          <img
            src={CodeDesignBrandIcon}
            alt="CodeDesign"
            className="w-8 h-8 cursor-pointer transition-opacity duration-150 group-hover:opacity-0"
          />
        </button>

        <div className="flex-1 flex items-center px-4 gap-4 min-w-0">
          <NavbarSection className="flex items-center -ml-1 min-w-0 flex-1 lg:flex-none">
            <div className="flex items-center sm:gap-2 -ml-2 min-w-0">
              {sortedProjects.length >= 1 ? (
                <Dropdown>
                  {({ open }) => (
                    <>
                      <DropdownButton
                        as="button"
                        className="flex items-center justify-between gap-1.5 px-2 text-sm font-medium text-white truncate w-[200px] p-0 bg-transparent border-0 hover:opacity-80 transition-opacity cursor-pointer data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2 aria-[expanded=true]:bg-white/10 aria-[expanded=true]:rounded-lg aria-[expanded=true]:px-2 aria-[expanded=true]:py-1.5"
                      >
                        <span className="flex items-center gap-2.5">
                          {renderProjectIcon(projectName, 24)}
                          <span className="truncate max-w-[130px]">
                            {projectName}
                          </span>
                        </span>
                        <ChevronDownIcon
                          className={`w-4 h-4 text-[#D4D4D8] flex-shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                        />
                      </DropdownButton>
                      <DropdownMenu className="w-full !flex !flex-col !p-1 !overflow-hidden">
                        <div className="!w-full">
                          <div className="relative w-full">
                            <input
                              value={projectSearch}
                              onChange={e => setProjectSearch(e.target.value)}
                              placeholder="Search sites"
                              className="w-full rounded-lg border border-[#E4E4E7] bg-white py-2 pr-10 pl-3 text-sm font-medium text-[#09090B] placeholder:text-[#A1A1AA] focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                            />
                            {projectSearch ? (
                              <button
                                type="button"
                                onClick={() => setProjectSearch("")}
                                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-[#71717A] hover:text-[#18181B] focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                aria-label="Clear search"
                              >
                                <XMarkIcon className="w-4 h-4" />
                              </button>
                            ) : (
                              <MagnifyingGlassIcon className="w-4 h-4 text-[#A1A1AA] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                            )}
                          </div>
                        </div>

                        {projectsLoading ? (
                          <div className="px-4 py-3 text-sm text-[#71717A] bg-[#FAFAFA]">
                            Loading projects...
                          </div>
                        ) : (
                          <div className="bg-[#FAFAFA] flex flex-col w-full">
                            {visibleProjects.length === 0 ? (
                              <div className="px-5 py-3 text-sm text-[#71717A]">
                                No sites found
                              </div>
                            ) : (
                              visibleProjects.map(project => {
                                const projectId = project._id || project.id;
                                const icon =
                                  project.favIcon ||
                                  project.projectMeta?.favIcon;
                                const initial =
                                  String(project.name || "P")
                                    .trim()
                                    .charAt(0)
                                    .toUpperCase() || "P";
                                return (
                                  <DropdownItem
                                    as="button"
                                    key={projectId}
                                    onClick={() => handleProjectSelect(project)}
                                    className={`!w-full !px-5 !py-2.5 rounded-lg transition-colors !bg-[#FAFAFA] hover:!bg-[#EBEBEB]`}
                                  >
                                    <div className="flex items-center gap-3 group">
                                      {icon ? (
                                        <img
                                          src={icon}
                                          alt={
                                            project.name || "Project favicon"
                                          }
                                          className="h-[20px] w-[20px] rounded-full border border-[#89898A] bg-white object-cover"
                                          onError={e => {
                                            e.currentTarget.onerror = null;
                                            e.currentTarget.removeAttribute(
                                              "src"
                                            );
                                          }}
                                        />
                                      ) : (
                                        <span className="flex h-[20px] w-[20px] shrink-0 items-center justify-center rounded-full bg-[#89898A] text-[10px] uppercase font-medium text-[#FFFFFF] group-hover:bg-[#18181B] transition-colors duration-150">
                                          {initial}
                                        </span>
                                      )}
                                      <span
                                        title={
                                          project.name || "Untitled Project"
                                        }
                                        className="truncate max-w-[170px] text-sm font-medium text-[#09090B] group-hover:text-[#09090B] transition-colors duration-150"
                                      >
                                        {project.name || "Untitled Project"}
                                      </span>
                                    </div>
                                  </DropdownItem>
                                );
                              })
                            )}
                          </div>
                        )}

                        <div className="w-full">
                          <DropdownItem
                            as="button"
                            onClick={handleGoToWorkspaces}
                            className="w-full !px-5 !py-2 !flex text-sm !font-semibold !text-[#71717A] hover:!text-[#09090B] hover:bg-[#F4F4F5]"
                          >
                            <span className="flex items-center gap-3 w-full">
                              <FolderIcon className="!w-4 !h-4 !text-[#71717A] group-hover:!text-[#09090B]" />
                              View all sites
                            </span>
                            <ArrowUpRightIcon className="!w-5 !h-5 !text-[#71717A] group-hover:!text-[#09090B] !mr-[-11px]" />
                          </DropdownItem>
                          <div className="border-t border-[#E4E4E7] w-full my-1" />
                          <DropdownItem
                            as="button"
                            onClick={() => setIsCreateDialogOpen(true)}
                            className="w-full !px-5 !py-2 !flex items-center gap-1 text-sm font-semibold text-[#09090B] hover:bg-[#F4F4F5]"
                          >
                            <PlusIcon className="w-5 h-5 group-hover:!text-[#09090B]" />
                            Create a new site
                          </DropdownItem>
                        </div>
                      </DropdownMenu>
                    </>
                  )}
                </Dropdown>
              ) : (
                <span className="text-sm font-medium text-white truncate">
                  {projectName}
                </span>
              )}
            </div>

            {/* View switcher: Overview ↔ Website Map (sitemap projects) */}
            <div className="hidden lg:flex items-center gap-1 ml-1 h-[61px]">
              {[
                { label: "Overview", path: "/overview", show: true },
                {
                  label: "Website Studio",
                  path: "/sitemap",
                  // The Studio is the editing surface for every sitemap-first
                  // project, paid or not. (It was unpaid-only during the
                  // limited rollout, which hid it from paying customers.)
                  show: Boolean(projectFromOverview.hasSitemap),
                },
              ]
                .filter(view => view.show)
                .map(view => {
                  const isActive = location.pathname === view.path;
                  return (
                    <button
                      key={view.path}
                      type="button"
                      onClick={() => {
                        if (isActive) return;
                        const params = new URLSearchParams();
                        if (workspaceId) params.set("workspace", workspaceId);
                        if (currentProjectId) params.set("project", currentProjectId);
                        if (view.path === "/sitemap" && uniqueUrl)
                          params.set("uniqueUrl", uniqueUrl);
                        if (sharedParamPresent) params.set("shared", "true");
                        navigate(`${view.path}?${params.toString()}`);
                      }}
                      className={`flex h-full cursor-pointer items-center border-b-2 px-2 text-sm transition-colors ${
                        isActive
                          ? "border-white font-semibold text-white"
                          : "border-transparent font-medium text-[#A1A1AA] hover:text-white"
                      }`}
                    >
                      {view.label}
                    </button>
                  );
                })}
            </div>

            {/* Website Studio projects never see the legacy/editor button —
                their surface is the Website Studio tab, and they are never
                handed off to the legacy builder. Paying does not change
                that (it used to: this was unpaid-only). */}
            {!projectFromOverview.hasSitemap && (
              <Button
                onClick={handleOpenDesignEditor}
                className="!hidden lg:!inline-flex items-center !text-sm !font-semibold !bg-[#52525B] !shadow-[0px_2px_0px_0px_#FFFFFF26_inset] !border !border-[#3F3F46] data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2"
              >
                <TvIcon className="w-5 h-5 !text-[#D4D4D8]" />
                <span>
                  {isAiCompanionMode
                    ? "Design Editor (AI Companion)"
                    : "Design Editor"}
                </span>
              </Button>
            )}
          </NavbarSection>

          <NavbarSection className="flex items-center justify-end flex-1 flex-shrink-0">
            {/* Admin mode indicator */}
            {isAdminMode && (
              <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/20 border border-amber-500/40 mr-2">
                <ShieldCheckIcon className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-xs font-semibold text-amber-400 truncate max-w-[120px]">
                  {targetUserLabel || targetUserId}
                </span>
              </div>
            )}
            {(() => {
              const isProjectLicensed =
                projectFromOverview.order?.paid || currentProject?.order?.paid;
              const hasWorkspacePlan = workspaceActivePlans.length > 0;

              if (isProjectLicensed) {
                // State 1: Project has a license — show "Licensed (Plan Name)"
                return (
                  <button
                    onClick={handleOpenPricingModal}
                    className="flex items-center gap-2 rounded-lg bg-[#18181B] border border-[#3F3F46] shadow-sm cursor-pointer whitespace-nowrap flex-shrink-0 hover:bg-[#27272A] transition-colors"
                    style={{ padding: "6px 10px" }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="10"
                      viewBox="0 0 14 10"
                      fill="none"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M6.625 0.625L9.29167 4.625L12.625 1.95833L11.2917 8.625H1.95833L0.625 1.95833L3.95833 4.625L6.625 0.625Z"
                        stroke="#A1A1AA"
                        strokeWidth="1.25"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span
                      className="text-sm font-semibold leading-5 text-[#A1A1AA]"
                      style={{ fontFamily: "Inter, sans-serif" }}
                    >
                      Licensed ({primaryPlanName})
                    </span>
                  </button>
                );
              }

              if (hasWorkspacePlan) {
                // State 2: Workspace has plan but project is unlicensed — show "Assign License"
                return (
                  <button
                    onClick={handleOpenPricingModal}
                    className="flex items-center gap-2 rounded-lg bg-[#18181B] border border-[#3F3F46] shadow-sm cursor-pointer whitespace-nowrap flex-shrink-0 hover:bg-[#27272A] transition-colors"
                    style={{ padding: "6px 10px" }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="10"
                      viewBox="0 0 14 10"
                      fill="none"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M6.625 0.625L9.29167 4.625L12.625 1.95833L11.2917 8.625H1.95833L0.625 1.95833L3.95833 4.625L6.625 0.625Z"
                        stroke="#A1A1AA"
                        strokeWidth="1.25"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span
                      className="text-sm font-semibold leading-5 text-[#A1A1AA]"
                      style={{ fontFamily: "Inter, sans-serif" }}
                    >
                      Assign License
                    </span>
                  </button>
                );
              }

              // State 3: No workspace plan — show "Upgrade now"
              return (
                <>
                  <button
                    onClick={handleOpenPricingModal}
                    className="flex items-center gap-1 px-3 py-1.5 cursor-pointer rounded-lg bg-[#18181B] border border-[#3F3F46] text-sm font-semibold text-[#A1A1AA] shadow-[0px_1px_2px_0px_#0000000D] whitespace-nowrap flex-shrink-0"
                    title="Free"
                  >
                    <CrownIcon className="w-3.5 h-3.5" />
                    <span>Free</span>
                  </button>
                  <PermissionGuard permission={PERMISSIONS.UPGRADE_NOW}>
                    <Button
                      onClick={handleOpenPricingModal}
                      className="!hidden lg:!inline-flex !text-sm !font-semibold !shadow-[0px_2px_0px_0px_#FFFFFF26_inset] [--btn-bg:#52525B] [--btn-border:#3F3F46] data-hover:[--btn-bg:#52525B] data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2"
                    >
                      Upgrade now
                    </Button>
                  </PermissionGuard>
                </>
              );
            })()}

            <Button
              plain
              onClick={handlePublishClick}
              className="!hidden lg:!inline-flex items-center !px-4 !bg-[#5C33FF] hover:!bg-[#5C33FF]/90 !rounded-lg !text-sm !font-semibold !shadow-[0px_2px_0px_0px_#FFFFFF26_inset] data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2"
            >
              Publish Website
              <ArrowUpRightIcon className="w-4 h-4 !text-white !ml-auto" />
            </Button>

            <Dropdown>
              <DropdownButton
                as="button"
                className="sm:-mr-4 -mr-2 bg-black flex items-center justify-center w-8 h-8 rounded-full overflow-hidden cursor-pointer data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2"
              >
                {typeof profile?.avatar === "string" &&
                profile.avatar.startsWith("http") ? (
                  <img
                    src={profile.avatar}
                    alt={profile.name || profile.email || "User avatar"}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-xs font-medium text-white">
                    {(profile?.name || profile?.email || "P")
                      .charAt(0)
                      .toUpperCase()}
                  </span>
                )}
              </DropdownButton>
              <DropdownMenu className="!p-0 !rounded-xl !overflow-hidden !w-[192px] !flex !flex-col">
                <div className="px-1 pb-1 pt-4 lg:hidden">
                  <div className="px-3 text-sm font-semibold text-[#18181B]">
                    {hasPublished ? "Published" : "Not published"}
                  </div>

                  {daysAgoLabel && (
                    <div className="px-3 text-xs font-medium text-[#71717A] mt-1">
                      {daysAgoLabel}
                    </div>
                  )}

                  <div className="lg:!hidden mt-3 mx-3">
                    <Button
                      plain
                      onClick={handlePublishClick}
                      className="!flex !w-full items-center !px-4 !py-1.5 !bg-[#5C33FF] hover:!bg-[#5C33FF]/90 !text-white !rounded-lg !text-sm !font-semibold !shadow-[0px_2px_0px_0px_#FFFFFF26_inset] data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2"
                    >
                      Publish Now
                      <ArrowUpRightIcon className="w-4 h-4 !text-white !ml-auto" />
                    </Button>
                  </div>

                  <div className="border-t border-[#E4E4E7] mb-1.5 mt-3" />
                  <PermissionGuard permission={PERMISSIONS.UPGRADE_NOW}>
                    {plan === "Free" && (
                      <button
                        onClick={handleOpenPricingModal}
                        className="w-full px-3 text-left text-sm font-medium text-[#18181B] hover:bg-zinc-950/5 p-2 rounded-lg data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2"
                      >
                        Upgrade
                      </button>
                    )}
                  </PermissionGuard>
                  <button
                    className="w-full px-3 text-left text-sm font-medium text-[#18181B] hover:bg-zinc-950/5 p-2 rounded-lg disabled:text-[#A1A1AA] disabled:cursor-not-allowed data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2"
                    onClick={handleOpenPublished}
                    disabled={!publishedHref}
                  >
                    Go to site
                  </button>
                  <div className="border-t border-[#E4E4E7] mt-1.5" />
                </div>

                <div className="py-0.5 sm:py-1 px-1 flex flex-col">
                  <div className="flex flex-col -mt-2 lg:mt-0 gap-1 p-2 border-b-[1.5px] border-[#E4E4E7]">
                    <div className="text-sm text-[#09090B] font-medium text-left px-1">
                      {profile?.name}
                    </div>
                    <div className="text-xs text-[#71717A] font-medium text-left px-1 truncate">
                      {profile?.email}
                    </div>
                  </div>
                  <DropdownItem onClick={handleGoToProfile}>
                    <div className="flex -my-0.5 sm:my-0 sm:py-0.5 items-center gap-3 text-sm text-[#09090B]">
                      <UserCircleIcon className="w-4 h-4 text-[#818182]" />
                      <span>My Profile</span>
                    </div>
                  </DropdownItem>
                  {aiConnectionsEnabled && (
                    <DropdownItem onClick={handleGoToAiConnections}>
                      <div className="flex -my-0.5 sm:my-0 sm:py-0.5 items-center gap-3 text-sm text-[#09090B]">
                        <SparklesIcon className="w-4 h-4 text-[#818182]" />
                        <span>ChatGPT &amp; Claude</span>
                      </div>
                    </DropdownItem>
                  )}
                  <DropdownItem onClick={handleGoToManageWorkspaces}>
                    <div className="flex -my-0.5 sm:my-0 sm:py-0.5 items-center gap-3 text-sm text-[#09090B]">
                      <HomeModernIcon className="w-4 h-4 text-[#818182]" />
                      <span>Workspaces</span>
                    </div>
                  </DropdownItem>
                  <DropdownItem onClick={handleOpenHelpSupport}>
                    <div className="flex -my-0.5 sm:my-0 sm:py-0.5 items-center gap-3 text-sm text-[#09090B]">
                      <QuestionMarkCircleIcon className="w-4 h-4 text-[#818182]" />
                      <span>Help Docs</span>
                    </div>
                  </DropdownItem>
                  <DropdownItem onClick={() => openIntercomChat()}>
                    <div className="flex -my-0.5 sm:my-0 sm:py-0.5 items-center gap-3 text-sm text-[#09090B]">
                      <ChatBubbleLeftRightIcon className="w-4 h-4 text-[#818182]" />
                      <span>Chat with Support</span>
                    </div>
                  </DropdownItem>
                  <DropdownItem onClick={handleOpenFeatureRequest}>
                    <div className="flex -my-0.5 sm:my-0 sm:py-0.5 items-center gap-3 text-sm text-[#09090B]">
                      <MegaphoneIcon className="w-4 h-4 text-[#818182]" />
                      <span>Feature Request</span>
                    </div>
                  </DropdownItem>
                  <DropdownItem onClick={handleOpenWhatsNew}>
                    <div className="flex -my-0.5 sm:my-0 sm:py-0.5 items-center gap-3 text-sm text-[#09090B]">
                      <GiftIcon className="w-4 h-4 text-[#818182]" />
                      <span>What&apos;s New</span>
                    </div>
                  </DropdownItem>
                  <DropdownItem onClick={handleSignOut}>
                    <div className="flex -my-0.5 sm:my-0 sm:py-0.5 items-center gap-3 text-sm text-[#09090B]">
                      <ArrowRightStartOnRectangleIcon className="w-4 h-4 text-[#818182]" />
                      <span>Sign out</span>
                    </div>
                  </DropdownItem>
                </div>
              </DropdownMenu>
            </Dropdown>
          </NavbarSection>
        </div>
      </div>

      <CreateProjectDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
      />
      <PublishDialog
        isOpen={showPublishDialog}
        onClose={() => setShowPublishDialog(false)}
        onPublishSuccess={() => refetch?.()}
        projectName={projectName}
        publishedHref={publishedHref}
        uniqueUrl={uniqueUrl}
      />
      <PricingIframeModal />
    </>
  );
}
